import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { CATALOG } from "@/data/catalog";
import { mediaUrl } from "@/data/pdpMedia";
import { trackEvent } from "@/lib/track";
import { track } from "@/components/common/Analytics";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "auraglow_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((sku, quantity = 1, openDrawer = true) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.sku === sku);
      if (existing) {
        return prev.map((i) => (i.sku === sku ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, { sku, quantity }];
    });
    const p = CATALOG[sku];
    if (p) {
      trackEvent("add_to_cart", { value: p.price * quantity, meta: { sku, quantity } });
      track("AddToCart", {
        content_ids: [sku], content_type: "product", content_name: p.name,
        value: p.price * quantity, currency: "AUD", num_items: quantity,
      });
    }
    if (openDrawer) setOpen(true);
  }, []);

  const updateQty = useCallback((sku, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.sku !== sku)
        : prev.map((i) => (i.sku === sku ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((sku) => {
    setItems((prev) => prev.filter((i) => i.sku !== sku));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  // Cart/checkout thumbnails use the same "Main image" the hero/PDP shows (admin-overridable).
  const CART_IMAGE_SLOT = { single: "pdp_gallery_1" };
  const detailed = items
    .map((i) => {
      const p = CATALOG[i.sku];
      if (!p) return { quantity: i.quantity };
      const slot = CART_IMAGE_SLOT[i.sku];
      return { ...p, image: (slot && mediaUrl(slot)) || p.image, quantity: i.quantity };
    })
    .filter((i) => i.sku);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = detailed.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = {
    items, detailed, count, subtotal,
    addItem, updateQty, removeItem, clear,
    open, setOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
